<div class="confirmation-dialog">
  <div class="flex items-center">
    <div class="mr-3">
      <ic-icon [icon]="icWarning" class="text-red-500" size="24px"></ic-icon>
    </div>
    <h2 mat-dialog-title class="headline m-0 pr-6 flex-auto">Confirmation de suppression du contact</h2>
    <button class="text-secondary" mat-dialog-close type="button" mat-icon-button>
      <ic-icon [icon]="icClose"></ic-icon>
    </button>
  </div>

  <mat-dialog-content class="content">
    <!-- Spinner de chargement -->
    <div *ngIf="loading" class="loading-container">
      <mat-spinner diameter="40"></mat-spinner>
      <p class="text-secondary mt-4">Analyse des impacts en cours...</p>
    </div>

    <!-- Erreur de chargement -->
    <div *ngIf="error && !loading" class="error-container">
      <p class="text-red-600 mb-4">Erreur lors de la récupération des impacts.</p>
      <p class="text-secondary text-sm">Voulez-vous tout de même supprimer ce contact ?</p>
    </div>

    <!-- Contenu chargé -->
    <ng-container *ngIf="!loading && !error && impactData">
      <div class="mb-6">
        <p class="text-secondary text-base mb-4">
          Vous êtes sur le point de supprimer le contact
          <strong>{{ getContactFullName() }}</strong>.
          Cette action est irréversible.
        </p>
      </div>

      <!-- Informations du contact -->
      <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <h3 class="text-lg font-semibold text-red-800 mb-3 flex items-center">
          Contact à supprimer
        </h3>
        <div class="grid grid-cols-1 gap-2 text-sm">
          <div>
            <strong>Nom :</strong>
            {{ getContactFullName() }}
          </div>
          <div>
            <strong>Email :</strong>
            {{ impactData.contact.email }}
          </div>
          <div>
            <strong>Téléphone :</strong>
            {{ formatPhoneNumbers(impactData.contact.phoneNumbers) }}
          </div>
        </div>
      </div>

      <!-- Établissements et bâtiments impactés -->
      <div *ngIf="impactData.establishments.length > 0" class="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
        <h3 class="text-lg font-semibold text-orange-800 mb-4 flex items-center">
          <ic-icon [icon]="icBusiness" class="mr-2" size="20px"></ic-icon>
          Établissements et bâtiments impactés
        </h3>

        <div class="space-y-4">
          <div
            *ngFor="let establishment of impactData.establishments"
            class="border border-orange-300 rounded-lg p-3 bg-white"
          >
            <h4 class="font-semibold text-base text-gray-800 flex items-center">
              <ic-icon [icon]="icBusiness" class="mr-2 text-orange-600" size="18px"></ic-icon>
              {{ establishment.label }}
            </h4>

            <div *ngIf="establishment.buildings.length > 0" class="ml-8 mt-2">
              <ul class="list-none text-sm text-gray-600 space-y-1">
                <li *ngFor="let building of establishment.buildings" class="flex items-center">
                  <ic-icon [icon]="icDomain" class="mr-2 text-gray-400" size="16px"></ic-icon>
                  {{ building.label }}
                </li>
              </ul>
            </div>

            <div *ngIf="establishment.buildings.length === 0" class="ml-8 mt-2">
              <p class="text-sm text-gray-400 italic">Aucun bâtiment associé</p>
            </div>
          </div>
        </div>

        <p class="text-xs text-orange-600 mt-3 font-medium">
          {{ impactData.totalEstablishmentsImpacted }} établissement{{ impactData.totalEstablishmentsImpacted > 1 ? 's' : '' }}
          et {{ impactData.totalBuildingsImpacted }} bâtiment{{ impactData.totalBuildingsImpacted > 1 ? 's' : '' }}
          impacté{{ impactData.totalBuildingsImpacted > 1 ? 's' : '' }}
        </p>
      </div>

      <!-- Dépendances existantes -->
      <div *ngIf="impactData.hasDependencies" class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <h3 class="text-lg font-semibold text-yellow-800 mb-4 flex items-center">
          <ic-icon [icon]="icAssignment" class="mr-2" size="20px"></ic-icon>
          Dépendances existantes
        </h3>

        <div class="space-y-3">
          <div *ngFor="let dep of getDependenciesWithData()" class="text-sm">
            <strong class="text-gray-700">{{ dep.name }} :</strong>
            <span class="text-gray-600 ml-1">{{ dep.dependencies.join(', ') }}</span>
          </div>
        </div>

        <p class="text-xs text-yellow-700 mt-3">
          <strong>Note :</strong>
          Les commandes, devis et engagements associés ne seront pas supprimés mais perdront la référence à ce contact.
        </p>
      </div>

      <!-- Récapitulatif -->
      <div class="bg-gray-100 border border-gray-300 rounded-lg p-4">
        <h3 class="text-lg font-semibold text-gray-800 mb-2">Récapitulatif</h3>
        <div class="text-sm space-y-1">
          <div *ngIf="impactData.totalEstablishmentsImpacted > 0">
            <span class="font-medium">{{ impactData.totalEstablishmentsImpacted }}</span>
            établissement{{ impactData.totalEstablishmentsImpacted > 1 ? 's' : '' }} concerné{{ impactData.totalEstablishmentsImpacted > 1 ? 's' : '' }}
          </div>
          <div *ngIf="impactData.totalBuildingsImpacted > 0">
            <span class="font-medium">{{ impactData.totalBuildingsImpacted }}</span>
            bâtiment{{ impactData.totalBuildingsImpacted > 1 ? 's' : '' }} impacté{{ impactData.totalBuildingsImpacted > 1 ? 's' : '' }}
          </div>
          <div *ngFor="let dep of getDependenciesWithData()">
            <span class="font-medium">{{ dep.dependencies.length }}</span>
            {{ dep.name.toLowerCase() }} associée{{ dep.dependencies.length > 1 ? 's' : '' }}
          </div>
        </div>
      </div>
    </ng-container>
  </mat-dialog-content>

  <mat-dialog-actions class="actions">
    <button mat-button type="button" (click)="onCancel()" class="mr-4">Annuler</button>
    <button mat-stroked-button color="warn" type="button" (click)="onUnitaryDelete()" [disabled]="loading" class="mr-4">
      Supprimer unitairement
    </button>
    <button mat-flat-button color="warn" type="button" (click)="onConfirm()" [disabled]="loading">
      Supprimer tout
    </button>
  </mat-dialog-actions>
</div>
